import type { PhenomenonSingle } from '@protector/entities/methodology/models';
import type {
  CurrentSeasonAreas,
  IndicatorDTO,
  MethodologyResponse,
  Phenomenon,
  PhenomenonPage
} from '@protector/entities/phenomenon/models';
import type { DateISOString, UUID } from '@protector/utils/types';
import axios from 'axios-observable';
import type { AxiosObservable } from 'axios-observable/dist/axios-observable.interface';
import { CORE_SERVICES_API_URL, PROTECTOR_API_URL } from 'config/constants';
import moment from 'moment';
import type { MonitoringDetailPhenomenon } from 'pages/timeline/monitoring.models';
import type { Observable } from 'rxjs';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/internal/operators/map';

const protectorApiUrl = PROTECTOR_API_URL;
const coreServicesApiUrl = CORE_SERVICES_API_URL;
const phenomenonUrl = `${protectorApiUrl}/api/v1/companies`;
const phenomenaByIdsUrl = `${protectorApiUrl}/api/v1/phenomena/ids`;
const samplePointsUrl = `${protectorApiUrl}/v1/analytics/monitoring/detail`;
const indicatorUrl = `${protectorApiUrl}/api/v1/indicators`;
const demeterMethodologyUrl = `${protectorApiUrl}/api/v1/methodologies`;

export const getPhenomenaByIds = (ids: string[]) => {
  return axios.post<Phenomenon[]>(phenomenaByIdsUrl, { ids }).pipe(map(response => response.data));
};

export const getIndicatorInfo = (id: UUID) => {
  return axios.get<IndicatorDTO>(`${indicatorUrl}/${id}`).pipe(map(response => response.data));
};

const fetchPhenomenaPage = async (companyId: string, page: number): Promise<PhenomenonPage> => {
  const response = await axios.get<PhenomenonPage>(`${phenomenonUrl}/${companyId}/phenomenons?page=${page}&size=500`).toPromise();
  return response.data;
};

export const getPhenomena = async (companyId: string): Promise<Phenomenon[]> => {
  const fromStorage = sessionStorage.getItem(`phenomenon_${companyId}`);

  if (fromStorage) {
    return JSON.parse(fromStorage) as Phenomenon[];
  }

  const initialResponse = await fetchPhenomenaPage(companyId, 0);

  let phenomenons = initialResponse.content;
  const totalPages = initialResponse.total_pages;

  const pagePromises: Promise<Phenomenon[]>[] = [];

  for (let currentPage = 1; currentPage < totalPages; currentPage++) {
    pagePromises.push(fetchPhenomenaPage(companyId, currentPage).then(response => response.content));
  }

  const allPages = await Promise.all(pagePromises);

  phenomenons = phenomenons.concat(...allPages);

  try {
    sessionStorage.setItem(`phenomenon_${companyId}`, JSON.stringify(phenomenons));
  } catch {
    return phenomenons;
  }

  return phenomenons;
};

export const getMethodologies = (propertyId: UUID, date?: string): Observable<CurrentSeasonAreas[]> => {
  const selectedDate = date ? parseSeasonAreaDate(date).toISOString().slice(0, 10) : moment().format('YYYY-MM-DD');
  const methodologiesUrl = `${coreServicesApiUrl}/v1/properties/${propertyId}/current-season-areas?date=${selectedDate}&includeMethodology=true`;

  return axios.get<CurrentSeasonAreas[]>(methodologiesUrl).pipe(map(response => response.data));
};

export const getPhenomenaByMethodology = (methodologyIds: UUID[]): Observable<Record<string, Phenomenon[]>[]> => {
  return forkJoin(
    methodologyIds.map(id => {
      const methodologyUrl = `${protectorApiUrl}/api/v1/methodologies/${id}/phenomenon`;
      return axios.get<Phenomenon[]>(methodologyUrl).pipe(
        map(response => response.data),
        map(phenomena => {
          return { [id]: phenomena };
        })
      );
    })
  );
};

const parseSeasonAreaDate = (endsAt?: string): Date => {
  if (!endsAt) return new Date();
  // Season-area endsAt uses the "+0000" offset format, which is not valid ISO 8601
  // and is rejected by some engines -- normalize to "+00:00" before parsing.
  const normalized = endsAt.replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

export const getMethodology = (methodologyId: UUID) => {
  return axios.get<MethodologyResponse>(`${protectorApiUrl}/api/v1/methodologies/${methodologyId}`).pipe(map(response => response.data));
};

export const getPhenomenaSamplePoints = (
  property_id: UUID,
  area_id: UUID,
  start: DateISOString,
  end: DateISOString,
  include_default_characteristics = false
) =>
  axios
    .get<{ phenomena: MonitoringDetailPhenomenon[] }>(`${samplePointsUrl}`, {
      params: { property_id, area_id, start, end, include_default_characteristics }
    })
    .pipe(
      map(response => {
        return response;
      })
    );

export const getSamplePointsData = async (
  property_id: UUID,
  area_id: UUID,
  start: DateISOString,
  end: DateISOString
): Promise<MonitoringDetailPhenomenon[]> => {
  const response = await getPhenomenaSamplePoints(property_id, area_id, start, end).toPromise();
  return response?.data?.phenomena ?? [];
};

export const getPhenomenaByMethodologyId = (methodologyId: UUID): AxiosObservable<PhenomenonSingle[]> => {
  return axios.get<PhenomenonSingle[]>(`${demeterMethodologyUrl}/${methodologyId}/phenomenon`);
};

export const getPhenomenaByCompany = (company_id: UUID) => {
  return axios.get<PhenomenonPage>(`${phenomenonUrl}/${company_id}/phenomenons?size=9999`).pipe(map(response => response.data));
};
